import type { ConnectionStatus } from "./webSocketContext"

/**
 * Application-level WebSocket close codes. Mirrors the backend's `WsCloseCodes`
 * object — keep in sync. RFC 6455 reserves 4000-4999 for private use.
 */
export const WS_CLOSE = {
    AUTH_TIMEOUT: 4002,
    ROOM_FULL: 4003,
    ROOM_NOT_FOUND: 4004,
    INVALID_NAME: 4005,
    INVALID_TOKEN: 4006,
    KICKED_BY_HOST: 4007,
    REPLACED: 4008,
    PROTOCOL_ERROR: 4009,
} as const

/**
 * The effects a close event should produce, decoupled from the provider so the
 * policy is unit-testable. `null` connection means "leave as-is" (only used with
 * `reconnect`, where the reconnect loop sets the state).
 */
export interface CloseDecision {
    connection: ConnectionStatus | null
    /** When defined, set the user-facing error string (or clear it with null). */
    error?: string | null
    /** Drop the stored rejoin token. */
    clearRejoin?: boolean
    /** Reset game state to the lobby. */
    reset?: boolean
    /** Start the auto-reconnect loop instead of setting a terminal state. */
    reconnect?: boolean
}

export interface CloseContext {
    /** A rejoin token is stored. */
    hasRejoin: boolean
    /**
     * The socket that just closed was for the same room our stored rejoin token
     * belongs to. This distinguishes "lost the room I'm in" from "typed a wrong
     * code on the join screen" — only the former should tear down our session.
     */
    isStoredRoom: boolean
}

/**
 * Pure mapping of a close (code, reason, context) to the decision the provider
 * applies. Authoritative refusals are terminal; abnormal closures reconnect when
 * the drop was for the room we hold a token for, else fail.
 *
 * Crucially, destructive teardown (clearing the rejoin token, resetting game
 * state) only happens when the close pertains to *our* room. A failed join to
 * some other code — a typo, a full room — must not wipe a session we still have.
 */
export function decideClose(code: number, reason: string, ctx: CloseContext): CloseDecision {
    switch (code) {
        case WS_CLOSE.REPLACED:
            // Superseded by a fresher session (another tab / refresh). Go quiet.
            return { connection: { kind: "idle" } }

        case WS_CLOSE.ROOM_FULL:
            // We weren't in this room — never destructive.
            return {
                connection: { kind: "failed", reason: reason || "Room is full." },
                error: reason || "That room is full.",
            }

        case WS_CLOSE.AUTH_TIMEOUT:
            return {
                connection: { kind: "failed", reason: reason || "Connection timed out." },
                error: reason || "The connection timed out. Please try again.",
            }

        case WS_CLOSE.ROOM_NOT_FOUND:
            return {
                connection: { kind: "failed", reason: reason || "Room not found." },
                error: reason || "That room is no longer available.",
                // Only tear down if the missing room is the one we hold a token for.
                clearRejoin: ctx.isStoredRoom,
                reset: ctx.isStoredRoom,
            }

        case WS_CLOSE.INVALID_NAME:
            return {
                connection: { kind: "failed", reason: reason || "Invalid name." },
                error: reason || "That name wasn't accepted.",
            }

        case WS_CLOSE.INVALID_TOKEN:
            // The token itself was rejected — it's useless, drop it.
            return {
                connection: { kind: "failed", reason: reason || "Session expired." },
                error: reason || "Your session expired — please rejoin.",
                clearRejoin: true,
                reset: true,
            }

        case WS_CLOSE.KICKED_BY_HOST:
            return {
                connection: { kind: "kicked", reason: reason || "Removed by host." },
                error: reason || "You were removed from the room by the host.",
                clearRejoin: true,
                reset: true,
            }

        case WS_CLOSE.PROTOCOL_ERROR:
            return {
                connection: { kind: "failed", reason: "Protocol error." },
                error: "There was a connection problem. Please try joining again.",
            }

        case 1000:
            // Graceful server-side close (room shut down / walk away politely).
            return { connection: { kind: "idle" }, clearRejoin: true, reset: true }

        default:
            // 1006 abnormal, 1011 server error, etc. Reconnect only if the drop
            // was for the room we have a token for; otherwise surface a failure.
            if (ctx.hasRejoin && ctx.isStoredRoom) return { connection: null, reconnect: true }
            return { connection: { kind: "failed", reason: "Connection lost." }, error: "The connection was lost." }
    }
}
